/**
 * Settings → Developer → API keys: one row a key, a dialog to make or change
 * one, and the new key shown once, to copy. What a key reaches is
 * docs/api.md's; the two switches are what it may do beyond the API.
 */

import { useEffect, useState } from 'react';
import { ApiError, createApiKey, deleteApiKey, listApiKeys, patchApiKey, type ApiKey } from '../../api';
import { ds } from '../../ds';
import { MonoLabel, Ph } from '../ui';
import { relativeTime } from './format';

const FAILED = 'That did not go through. Try again.';

/** The base URL an OpenAI client is given. */
const baseUrl = () => `${window.location.origin}/api/assist/v1`;

type Editing = { key: ApiKey } | { key: null };

/** `relativeTime`'s forms as a sentence: "Used 5 min ago", "Used yesterday". */
const usedText = (when: string): string =>
  when === 'Now' ? 'Used just now' : /min|hr/.test(when) ? `Used ${when} ago` : `Used ${when === 'Yesterday' ? 'yesterday' : when}`;

function KeyRow({ apiKey, onEdit }: { apiKey: ApiKey; onEdit: () => void }) {
  const reaches = [apiKey.chats ? 'Chats' : null, apiKey.memory ? 'Memory' : null].filter(Boolean).join(' · ');
  const used = apiKey.lastUsedAt ? usedText(relativeTime(apiKey.lastUsedAt)) : 'Not used yet';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minHeight: 58, padding: '0 16px', borderBottom: '1px solid var(--border-subtle)' }}>
      <span
        style={{
          width: 32,
          height: 32,
          flex: 'none',
          borderRadius: 9,
          background: 'var(--app-panel-sunken)',
          color: 'var(--text-muted)',
          display: 'grid',
          placeItems: 'center'
        }}
      >
        <Ph name="key" size={16} />
      </span>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <span style={{ fontSize: 14, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {apiKey.name}
        </span>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <span style={{ fontFamily: 'var(--font-mono)' }}>asst_…{apiKey.hint}</span>
          {reaches ? ` · ${reaches}` : ''} · {used}
        </span>
      </div>
      <button type="button" className="bare hov-strong" onClick={onEdit} style={{ fontSize: 13, color: 'var(--text-link)' }}>
        Edit
      </button>
    </div>
  );
}

/** The new key, once. */
function Created({ token }: { token: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderRadius: 'var(--radius-sm)', background: 'var(--app-panel-sunken)' }}>
        <code className="selectable" style={{ flex: 1, minWidth: 0, fontSize: 12.5, overflowWrap: 'anywhere', color: 'var(--text-strong)' }}>
          {token}
        </code>
        <button
          type="button"
          className="bare hov-strong"
          onClick={() => void navigator.clipboard?.writeText(token).then(() => setCopied(true))}
          style={{ flex: 'none', fontSize: 13, color: 'var(--text-link)' }}
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>This is the only time it is shown.</span>
    </div>
  );
}

function KeyDialog({ editing, onSaved, onRevoked, onClose }: {
  editing: Editing;
  onSaved: (key: ApiKey) => void;
  onRevoked: (id: string) => void;
  onClose: () => void;
}) {
  const { Dialog, Button, Input, Switch } = ds();
  const key = editing.key;
  const [name, setName] = useState(key?.name ?? '');
  const [chats, setChats] = useState(key?.chats ?? false);
  const [memory, setMemory] = useState(key?.memory ?? false);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);

  const fail = (err: unknown) => {
    setError(err instanceof ApiError ? err.message : FAILED);
    setConfirming(false);
    setBusy(false);
  };

  const save = () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    const fields = { name: name.trim(), chats, memory };
    if (key) {
      patchApiKey(key.id, fields)
        .then((saved) => {
          onSaved(saved);
          onClose();
        })
        .catch(fail);
      return;
    }
    createApiKey(fields)
      .then((made) => {
        onSaved(made.key);
        setToken(made.token);
        setBusy(false);
      })
      .catch(fail);
  };

  const revoke = () => {
    if (!key || busy) return;
    setBusy(true);
    deleteApiKey(key.id)
      .then(() => {
        onRevoked(key.id);
        onClose();
      })
      .catch(fail);
  };

  const footer = token ? (
    <>
      <span style={{ flex: 1 }} />
      <Button variant="primary" density="app" size="sm" onClick={onClose}>
        Done
      </Button>
    </>
  ) : confirming && key ? (
    <>
      <span style={{ flex: 1, alignSelf: 'center', fontSize: 13.5, color: 'var(--text-body)' }}>Revoke {key.name}?</span>
      <Button variant="ghost" density="app" size="sm" disabled={busy} onClick={() => setConfirming(false)}>
        Cancel
      </Button>
      <Button variant="danger" density="app" size="sm" disabled={busy} onClick={revoke}>
        Revoke
      </Button>
    </>
  ) : (
    <>
      {key ? (
        <Button variant="ghost" density="app" size="sm" disabled={busy} onClick={() => setConfirming(true)}>
          Revoke
        </Button>
      ) : null}
      <span style={{ flex: 1 }} />
      <Button variant="ghost" density="app" size="sm" disabled={busy} onClick={onClose}>
        Cancel
      </Button>
      <Button variant="primary" density="app" size="sm" disabled={busy || !name.trim()} onClick={save}>
        {key ? 'Save' : 'Create'}
      </Button>
    </>
  );

  return (
    <Dialog
      open
      position="fixed"
      width={520}
      title={token ? 'Your new key' : key ? key.name : 'New API key'}
      onClose={onClose}
      style={{ borderRadius: 'var(--radius-dialog)', padding: 24, gap: 16, maxHeight: '100%', overflowY: 'auto' }}
      footer={footer}
    >
      {token ? (
        <Created token={token} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} autoFocus={!key} />
          <Switch label="Chats" description="Reads and writes your chats." checked={chats} onChange={setChats} />
          <Switch label="Memory" description="Replies use your memory and add to it." checked={memory} onChange={setMemory} />
          {error ? <span style={{ fontSize: 12.5, color: 'var(--danger-600)' }}>{error}</span> : null}
        </div>
      )}
    </Dialog>
  );
}

export function ApiKeys() {
  const { Button } = ds();
  const [keys, setKeys] = useState<ApiKey[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [editing, setEditing] = useState<Editing | null>(null);

  useEffect(() => {
    let cancelled = false;
    listApiKeys()
      .then((list) => {
        if (!cancelled) setKeys(list.keys);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const saved = (key: ApiKey) =>
    setKeys((list) => (list?.some((k) => k.id === key.id) ? list.map((k) => (k.id === key.id ? key : k)) : [...(list ?? []), key]));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <MonoLabel>API keys</MonoLabel>
      {failed ? (
        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Keys did not load.</span>
      ) : keys?.length ? (
        <div style={{ background: 'var(--surface-card)', borderRadius: 'var(--radius-app-card)', overflow: 'hidden' }}>
          {keys.map((k) => (
            <KeyRow key={k.id} apiKey={k} onEdit={() => setEditing({ key: k })} />
          ))}
        </div>
      ) : keys ? (
        <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-muted)' }}>No keys yet.</p>
      ) : null}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12 }}>
        <Button variant="primary" density="app" size="sm" iconLeft="plus" disabled={!keys} onClick={() => setEditing({ key: null })}>
          New key
        </Button>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          Base URL <code className="selectable" style={{ fontSize: 12 }}>{baseUrl()}</code>
        </span>
      </div>
      {editing ? (
        <KeyDialog
          editing={editing}
          onSaved={saved}
          onRevoked={(id) => setKeys((list) => list?.filter((k) => k.id !== id) ?? null)}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}
